import React, { useEffect, useMemo, useState } from 'react';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Modal from '../Modal.jsx';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

/**
 * PERAKENDE (tezgah) SATIŞ — masada oturmayan müşteri.
 *
 * Tezgaha gelip "bir kahve, bir börek" diyen müşteri için masa açıp sipariş
 * girip hesaba gidip tahsil edip masayı kapatmak beş ekran demekti. Burada
 * tek ekran var: ürünleri seç, hesabı seç, tahsil et.
 *
 * Arkada ayrı bir "kasa" defteri YOK: satış `order`, para `payment` olarak
 * aynı deftere yazılıyor (bkz. apps/api/src/routes/counter.js) — ciro tek
 * yerde toplansın.
 *
 * Ekran garsonun sipariş ekranıyla (OrderEntry) aynı dokunuş mantığını
 * izliyor: ürüne dokun = sepete ekle, opsiyonlu ürün bir sayfa açar.
 */
export default function Counter() {
  const t = useT();

  const [menu, setMenu] = useState(null);
  const [data, setData] = useState(null);      // { accounts, sales }
  const [me, setMe] = useState(null);
  const [cart, setCart] = useState([]);
  const [cat, setCat] = useState(0);
  const [q, setQ] = useState('');
  const [sheetItem, setSheetItem] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);      // son satışın fişi
  const [tab, setTab] = useState('sell');      // sell | recent

  const loadSales = () => api('/api/counter-sales').then((d) => {
    setData(d);
    // Varsayılan hesap: ilk sıradaki (çoğu kasada «Nakit»).
    setAccountId((a) => a ?? d.accounts[0]?.id ?? null);
  });

  useEffect(() => {
    Promise.all([
      api('/api/menu').then(setMenu),
      api('/api/restaurant').then(setMe),
      loadSales(),
    ]).catch((e) => setErr(errMessage(t, e)));
    // eslint-disable-next-line
  }, []);

  const items = useMemo(() => {
    if (!menu) return [];
    const all = menu.categories.flatMap((c) => c.items.map((i) => ({ ...i, catId: c.id })));
    const needle = q.trim().toLowerCase();
    return all
      .filter((i) => (cat ? i.catId === cat : true))
      .filter((i) => (needle ? i.name.toLowerCase().includes(needle) : true));
  }, [menu, cat, q]);

  const currency = menu?.currency || me?.restaurant?.currency;
  const total = cart.reduce((a, c) => a + c.unit * c.qty, 0);
  const count = cart.reduce((a, c) => a + c.qty, 0);

  function addItem(item, options = [], itemNote = '') {
    const key = `${item.id}:${options.map((o) => o.id).sort().join(',')}:${itemNote}`;
    const unit = item.priceMinor + options.reduce((a, o) => a + o.priceDeltaMinor, 0);
    setErr(''); setDone(null);
    setCart((c) => {
      const hit = c.find((x) => x.key === key);
      if (hit) return c.map((x) => (x.key === key ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { key, item, options, qty: 1, unit, note: itemNote }];
    });
    setSheetItem(null);
  }
  const bump = (key, d) => setCart((c) => c
    .map((x) => (x.key === key ? { ...x, qty: x.qty + d } : x))
    .filter((x) => x.qty > 0));

  async function charge() {
    if (!cart.length || busy) return;
    setBusy(true); setErr(''); setDone(null);
    try {
      const out = await api('/api/counter-sales', {
        method: 'POST',
        body: {
          clientUuid: uuid(),        // çift dokunuş iki satış yazmasın
          accountId: accountId || undefined,
          note: note.trim() || undefined,
          items: cart.map((c) => ({
            menuItemId: c.item.id,
            qty: c.qty,
            optionIds: c.options.map((o) => o.id),
            note: c.note || undefined,
          })),
        },
      });
      setCart([]); setNote('');
      setDone(out);
      await loadSales();
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  if (err && !menu) return <p className="err">{err}</p>;
  if (!menu || !data) return <PageSkeleton shape="menu" />;

  return (
    <div className="oe">
      <div className="oe-head">
        <div>
          <b>{t('cnt.title')}</b>
          <span className="muted"> · {t('cnt.subtitle')}</span>
        </div>
        <span className="spacer" />
        <div className="seg oe-seg" role="tablist">
          <button role="tab" aria-selected={tab === 'sell'} onClick={() => setTab('sell')}>
            <Icon n="cart" />{t('cnt.sell')}
          </button>
          <button role="tab" aria-selected={tab === 'recent'} onClick={() => { setTab('recent'); loadSales(); }}>
            <Icon n="receipt" />{t('cnt.recent')}
          </button>
        </div>
      </div>

      {tab === 'recent' ? (
        <RecentSales sales={data.sales} currency={currency} t={t} />
      ) : (
        <>
          {done && (
            <div className="card">
              <div className="row">
                <b>{t('cnt.charged', { amount: money(done.payment.grossMinor, currency) })}</b>
                <span className="spacer" />
                <span className="muted">#{done.order.code}</span>
              </div>
              {done.leftoverMinor > 0 && (
                <p className="muted">{t('cnt.leftover', { amount: money(done.leftoverMinor, currency) })}</p>
              )}
            </div>
          )}

          <div className="oe-filter">
            <input className="oe-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('ord.search')} autoComplete="off" />
            <div className="chips">
              <button className={cat === 0 ? 'on' : ''} onClick={() => setCat(0)}>{t('ord.all')}</button>
              {menu.categories.map((c) => (
                <button key={c.id} className={cat === c.id ? 'on' : ''} onClick={() => setCat(c.id)}>{c.name}</button>
              ))}
            </div>
          </div>

          {items.length === 0 ? <p className="muted">{t('ord.noMatch')}</p> : (
            <div className="menugrid">
              {items.map((i) => (
                <button key={i.id} className="mtile" disabled={!i.available}
                  onClick={() => (i.optionGroups?.length ? setSheetItem(i) : addItem(i))}>
                  <span className="mname">{i.name}</span>
                  <span className="mprice">{money(i.priceMinor, currency)}</span>
                  {!i.available && <span className="moos">{t('ord.outOfStock')}</span>}
                  {!!i.optionGroups?.length && <span className="mopt">{t('ord.hasOptions')}</span>}
                </button>
              ))}
            </div>
          )}

          {err && <p className="err oe-flash">{err}</p>}

          {cart.length > 0 && (
            <div className="cartlist" style={{ position: 'static', marginTop: 16 }}>
              {cart.map((c) => (
                <div key={c.key} className="cartrow">
                  <span className="cname">
                    {c.item.name}
                    {!!c.options.length && <em> · {c.options.map((o) => o.name).join(', ')}</em>}
                    {c.note && <em> · “{c.note}”</em>}
                  </span>
                  <span className="cqty">
                    <button onClick={() => bump(c.key, -1)} aria-label="−"><Icon n="minus" /></button>
                    <b>{c.qty}</b>
                    <button onClick={() => bump(c.key, +1)} aria-label="+"><Icon n="plus" /></button>
                  </span>
                  <span className="cprice">{money(c.unit * c.qty, currency)}</span>
                </div>
              ))}

              <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 6 }}>
                {/* Paranın hangi kasaya girdiği: gün sonu sayımı buna bakıyor. */}
                {data.accounts.map((a) => (
                  <button key={a.id} className={accountId === a.id ? 'chip on' : 'chip'}
                    onClick={() => setAccountId(a.id)}>
                    <Icon n={a.icon || 'cash'} />{a.name}
                  </button>
                ))}
              </div>

              <input value={note} maxLength={300} placeholder={t('cnt.notePlaceholder')}
                onChange={(e) => setNote(e.target.value)} style={{ width: '100%', marginTop: 8 }} />
            </div>
          )}

          <div className="cartbar">
            <button className="cartinfo" disabled>
              <Icon n="cart" />
              <b>{t('ord.lines', { n: count })}</b>
              <span>{money(total, currency)}</span>
            </button>
            <button className="primary cartsend" disabled={busy || !cart.length} onClick={charge}>
              <Icon n="cash" />
              {busy ? t('cnt.charging') : t('cnt.charge', { amount: money(total, currency) })}
            </button>
          </div>
        </>
      )}

      {sheetItem && (
        <OptionSheet item={sheetItem} currency={currency} t={t}
          onClose={() => setSheetItem(null)} onAdd={(opts, n) => addItem(sheetItem, opts, n)} />
      )}
    </div>
  );
}

/** Son 24 saatin tezgah satışları. Ödenmemiş kalan (kopan istek) burada görünür. */
function RecentSales({ sales, currency, t }) {
  if (!sales.length) return <p className="muted">{t('cnt.noSales')}</p>;
  return (
    <div className="curlist">
      {sales.map((s) => (
        <div className="card" key={s.id}>
          <div className="row">
            <b>{new Date(s.openedAt).toLocaleTimeString()}</b>
            {s.dueMinor > 0
              ? <span className="badge warn">{t('cnt.unpaid', { amount: money(s.dueMinor, s.currency || currency) })}</span>
              : <span className="badge ok">{t('cnt.paid')}</span>}
            <span className="spacer" />
            <b>{money(s.totalMinor, s.currency || currency)}</b>
          </div>
          {s.orders.flatMap((o) => o.items).map((i, ix) => (
            <div className="currow" key={ix}>
              <span>{i.qty}× {i.nameSnapshot}</span>
              <span className="spacer" />
              <span className="muted">{money(i.lineTotalMinor, s.currency || currency)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Opsiyonlu ürün + mutfak notu. Kurallar sunucuda yeniden denetlenir. */
function OptionSheet({ item, currency, t, onClose, onAdd }) {
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState('');
  const has = (o) => picked.some((p) => p.id === o.id);

  const max = (g) => g.maxSelect || 1;
  const countIn = (list, g) => list.filter((x) => x.groupId === g.id).length;

  function toggle(group, opt) {
    setPicked((p) => {
      if (p.some((x) => x.id === opt.id)) return p.filter((x) => x.id !== opt.id);
      if (max(group) === 1) return [...p.filter((x) => x.groupId !== group.id), { ...opt, groupId: group.id }];
      if (countIn(p, group) >= max(group)) return p;
      return [...p, { ...opt, groupId: group.id }];
    });
  }

  const missing = (item.optionGroups || []).filter((g) => {
    const n = picked.filter((p) => p.groupId === g.id).length;
    return (g.required && n < Math.max(1, g.minSelect)) || n < g.minSelect;
  });
  const total = item.priceMinor + picked.reduce((a, o) => a + o.priceDeltaMinor, 0);

  return (
    <Modal title={item.name} subtitle={money(total, currency)} onClose={onClose}
      footer={
        <button className="primary block" disabled={missing.length > 0}
          onClick={() => onAdd(picked, note.trim())}>
          <Icon n={missing.length ? 'layers' : 'plus'} />
          {missing.length ? t('ord.pickRequired', { group: missing[0].name }) : t('ord.add')}
        </button>
      }>
      {(item.optionGroups || []).map((g) => (
        <div key={g.id} className="optgroup">
          <h3>
            {g.name}
            <span className="muted">
              {g.required ? ` · ${t('ord.required')}` : ''}
              {g.maxSelect > 1 ? ` · ${t('ord.upTo', { n: g.maxSelect })}` : ''}
            </span>
          </h3>
          <div className="optrow">
            {g.options.filter((o) => o.available).map((o) => (
              <button key={o.id} className={has(o) ? 'opt on' : 'opt'} onClick={() => toggle(g, o)}
                disabled={!has(o) && max(g) > 1 && countIn(picked, g) >= max(g)}>
                {o.name}
                {o.priceDeltaMinor !== 0 && <em> {o.priceDeltaMinor > 0 ? '+' : ''}{money(o.priceDeltaMinor, currency)}</em>}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="optgroup">
        <h3>{t('cnt.itemNote')}</h3>
        <input value={note} maxLength={200} placeholder={t('cnt.itemNotePlaceholder')}
          onChange={(e) => setNote(e.target.value)} style={{ width: '100%' }} />
      </div>
    </Modal>
  );
}
